import { useBoxPlanningSlots } from "@/modules/boxPlanning/hooks/useBoxPlanningSlots"
import { useBoxProgram } from "@/modules/boxProgrammation/hooks/useBoxProgram"
import { useBoxSlotScheduledSessionsInRange } from "@/modules/boxSlotSession/hooks/useBoxSlotSessionScheduled"
import { cn } from "@/modules/lib/utils"
import { BoxProgrammationSessionsCalendarDayColumn } from "@modules/boxProgrammationSessions/components/boxProgrammationSessionsCalendarDayColumn"
import { useCalendar } from "@modules/calendar/hooks/useCalendar"
import dayjs, { Dayjs } from "dayjs"
import { useCallback } from "react"

export function BoxProgrammationSessionsCalendar({
  boxProgramId,
  className,
  ...props
}: {
  boxProgramId: string
} & React.ComponentProps<"div">) {
  const { data: boxProgrammation } = useBoxProgram(boxProgramId)
  const { getCalendarRange, currentWeekdays } = useCalendar()

  const { startDate, endDate } = getCalendarRange()

  const { data: scheduledSessions } = useBoxSlotScheduledSessionsInRange(
    boxProgramId,
    dayjs(startDate).toDate(),
    dayjs(endDate).toDate(),
  )

  const { data: slots } = useBoxPlanningSlots(
    dayjs.isDayjs(dayjs(startDate)) ? dayjs(startDate).toDate() : undefined,
    dayjs.isDayjs(dayjs(endDate)) ? dayjs(endDate).toDate() : undefined,
    boxProgramId,
  )

  const getSlotsOnSelectedDay = useCallback(
    (value: Dayjs) => {
      return slots.filter(
        (slot) => slot.programmationId === boxProgrammation.id && dayjs(slot.startDateTime).isSame(dayjs(value), "day"),
      )
    },
    [slots, boxProgrammation.id],
  )

  return (
    <div className={cn("flex grow flex-col content-center", className)} {...props}>
      <div className="calendar relative">
        <table className="calendar-table">
          <thead>
            <tr>
              {currentWeekdays?.map((weekday) => (
                <th
                  className={`weekday-label weekday-label-${weekday.weekdayIndex} ${weekday.isToday ? "today" : ""}`}
                  key={weekday.weekdayIndex}
                >
                  {weekday.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              {currentWeekdays?.map((weekday, index) => (
                <BoxProgrammationSessionsCalendarDayColumn
                  key={index}
                  program={boxProgrammation}
                  date={weekday.date}
                  sessions={scheduledSessions.filter((session) => dayjs(session.date).isSame(weekday.date, "date"))}
                  slots={weekday.date ? getSlotsOnSelectedDay(weekday.date) : []}
                />
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}
